import { useState } from "react";
import type { DataTableTab } from "~components/DataTable/types/dataTableTab.ts";
import { GroupingConferenceDivision } from "~pages/Standings/enums/groupingConferenceDivision";
import { GroupingStagePool } from "~pages/Standings/enums/groupingStagePool";
import { sortNestedTables } from "~pages/Standings/StandingsListPage/utilities/sortNestedTables";
import type { StandingTransformed } from "~pages/Standings/types/standingTransformed";
import type { TransformedResult } from "~pages/Standings/types/transformedResult";

type StandingGroup = TransformedResult<StandingTransformed | TransformedResult<StandingTransformed>>;

const toTabs = (groups: TransformedResult<StandingTransformed>[]): DataTableTab<StandingTransformed>[] =>
  [...groups].sort(sortNestedTables).map((group) => ({
    key: group.entity?.id ?? "",
    label: group.entity?.name,
    dataSource: group.values,
  }));

export const useStandingsTableData = (
  data: Array<StandingTransformed | StandingGroup> | undefined,
  grouping?: GroupingStagePool,
  conferenceGrouping?: GroupingConferenceDivision,
) => {
  const [selectedParentKey, setSelectedParentKey] = useState<string>();

  const isGroupedByStage = grouping === GroupingStagePool.Stage || grouping === GroupingStagePool.StagePool;
  const isGroupedByConference =
    conferenceGrouping === GroupingConferenceDivision.Conference || conferenceGrouping === GroupingConferenceDivision.Division;
  const hasNestedGroups = grouping === GroupingStagePool.StagePool || conferenceGrouping === GroupingConferenceDivision.Division;

  if (!isGroupedByStage && !isGroupedByConference) {
    return { dataSource: (data ?? []) as StandingTransformed[], tabs: [] };
  }

  const groups = [...((data ?? []) as StandingGroup[])].sort(sortNestedTables);

  if (!hasNestedGroups) {
    return { dataSource: [], tabs: toTabs(groups as TransformedResult<StandingTransformed>[]) };
  }

  const parent = groups.find((group) => group.entity?.id === selectedParentKey) ?? groups[0];
  const parentSelect = {
    labelIntlKey: isGroupedByStage ? "stage" : "conference",
    value: parent?.entity?.id,
    options: groups.map((group) => ({ label: group.entity?.name ?? "", value: group.entity?.id ?? "" })),
    onChange: setSelectedParentKey,
  };

  // Standings outside of any stage or conference have no pools or divisions to split into tabs
  if (!parent || parent.entity?.id === "noKeyData") {
    return { parentSelect, dataSource: (parent?.values ?? []) as StandingTransformed[], tabs: [] };
  }

  return { parentSelect, dataSource: [], tabs: toTabs(parent.values as TransformedResult<StandingTransformed>[]) };
};
